import React, { useState } from "react";
import styles from "./Trains.module.scss";

import { TrainCard } from "./TrainCard/";

import { formatScheduleDate } from "@/shared/utils/";
import { Training } from "./model";
import classNames from "classnames";
import { ConfirmDeleteModal } from "@/features/confirm-delete-modal";

type TrainsProps = {
  trainings: Training[];
  isFirstTrain?: boolean;
  hasActiveSubscription?: boolean;
  trainingCount?: number;
  showDeleteButton?: boolean;
  onDeleteTraining?: (trainingId: number) => void;
  isAdmin?: boolean;
  adminUserId?: number;
  editBasePath?: string;
};

export const Trains: React.FC<TrainsProps> = ({
  trainings,
  isFirstTrain = false,
  hasActiveSubscription = false,
  trainingCount = 0,
  showDeleteButton = false,
  onDeleteTraining,
  isAdmin = false,
  adminUserId,
  editBasePath,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [trainingToDelete, setTrainingToDelete] = useState<number | null>(null);
  // Фильтруем тренировки по status: актуальные (status === 'wait' или status === null/undefined) и неактуальные (status !== 'wait' && status !== null/undefined)
  const currentTrainings = trainings
    ? trainings.filter((train) => {
        const status = train.status;
        // Актуальные: status === 'wait' или null/undefined
        return status === 'wait' || !status;
      })
    : [];

  const pastTrainings = trainings
    ? trainings.filter((train) => {
        const status = train.status;
        // Неактуальные: status !== 'wait' и status !== null/undefined (т.е. есть значение, но не 'wait')
        return status && status !== 'wait';
      })
    : [];

  // Определяем доступные тренировки: если нет подписки, доступны только первые training_count актуальных тренировок
  // Неактуальные тренировки всегда доступны (не блокируются)
  // Пользовательские тренировки (showDeleteButton === true) никогда не блокируются
  const accessibleTrainingIds = (() => {
    // Если это пользовательские тренировки, все доступны
    if (showDeleteButton) {
      return new Set(trainings?.map((train) => train.id) || []);
    }

    if (!trainings || trainings.length === 0) {
      return new Set<number>();
    }

    // Если есть подписка, все тренировки доступны
    if (hasActiveSubscription) {
      return new Set(trainings.map((train) => train.id));
    }

    // Если нет подписки, доступны только первые training_count актуальных тренировок
    // НО должно быть открыто НЕ МЕНЕЕ 3 тренировок
    // Сортируем актуальные тренировки по дате
    const sortedCurrentTrainings = [...currentTrainings].sort((a, b) => {
      const firstDate = new Date(a.schedule).getTime();
      const secondDate = new Date(b.schedule).getTime();
      return firstDate - secondDate;
    });

    const allowedCount = Math.max(3, trainingCount);
    const allowedTrainings = sortedCurrentTrainings.slice(0, allowedCount);

    // Добавляем все неактуальные тренировки в доступные (они не блокируются)
    const allowedIds = new Set(allowedTrainings.map((train) => train.id));
    pastTrainings.forEach((train) => allowedIds.add(train.id));

    return allowedIds;
  })();

  const isTrainingLocked = (train: Training) =>
    !accessibleTrainingIds.has(train.id);

  return (
    <div className={styles.root}>
      {trainings && trainings.length > 0 && (
        <>
          {isFirstTrain ? (
            currentTrainings.slice(0, 1).map((train, index) => (
              <div key={index}>
                <h2
                  className={classNames("trains__title", {
                    [styles.firstTrainTitle]: isFirstTrain,
                  })}
                >
                  {isFirstTrain
                    ? "Ближайшая тренировка"
                    : formatScheduleDate(train.schedule)}
                </h2>
                <TrainCard
                  index={train.id}
                  image={train.title_photo || "none"}
                  describtion="Описание"
                  duration={String(train.training_sample_id)}
                  title={train.title}
                  isLocked={isTrainingLocked(train)}
                  trainingType={showDeleteButton ? "free" : undefined}
                  isAdmin={isAdmin}
                  adminUserId={adminUserId}
                  editBasePath={editBasePath}
                />
              </div>
            ))
          ) : (
            <div style={{ marginTop: "25px", marginBottom: "25px" }}>
              {currentTrainings.map((train, index) => (
                <div key={index}>
                  <h2 className="trains__title">
                    {formatScheduleDate(train.schedule)}
                  </h2>
                  <TrainCard
                    index={train.id}
                    image={train.title_photo || "none"}
                    describtion="Описание"
                    duration={String(train.training_sample_id)}
                    title={train.title}
                    isLocked={isTrainingLocked(train)}
                    trainingType={showDeleteButton ? "free" : undefined}
                    onDelete={showDeleteButton && onDeleteTraining ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTrainingToDelete(train.id);
                      setDeleteModalOpen(true);
                    } : undefined}
                    isAdmin={isAdmin}
                    adminUserId={adminUserId}
                    editBasePath={editBasePath}
                  />
                </div>
              ))}
            </div>
          )}

          {!isFirstTrain && pastTrainings.length > 0 && (
            <div>
              <h2 className="trains__title">Неактуальные тренировки:</h2>
              {pastTrainings.map((train, index) => (
                <div key={index}>
                  <h2
                    className={"trains__title"}
                    style={{ textDecoration: "line-through", color: "#ff4b38" }}
                  >
                    {formatScheduleDate(train.schedule)}
                  </h2>
                  <div>
                    <TrainCard
                      index={train.id}
                      image={train.title_photo || "none"}
                      describtion="Описание"
                      duration={String(train.training_sample_id)}
                      title={train.title}
                      isLocked={isTrainingLocked(train)}
                      trainingType={showDeleteButton ? "free" : undefined}
                      onDelete={showDeleteButton && onDeleteTraining ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTrainingToDelete(train.id);
                        setDeleteModalOpen(true);
                      } : undefined}
                      isAdmin={isAdmin}
                      adminUserId={adminUserId}
                      editBasePath={editBasePath}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {deleteModalOpen && trainingToDelete !== null && onDeleteTraining && (
        <ConfirmDeleteModal
          onClose={() => {
            setDeleteModalOpen(false);
            setTrainingToDelete(null);
          }}
          onConfirm={() => {
            onDeleteTraining(trainingToDelete);
          }}
          title="Удаление тренировки"
          message="Вы уверены, что хотите удалить эту тренировку?"
        />
      )}
    </div>
  );
};
